import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faCheck,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faPenToSquare,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import type {
  CompetencyArea,
  Competency,
} from '../../types/GrowthWheelManagement'
import { trimDesc } from './wheelManagementUtils'
import { SubCompetencyRow } from './SubCompetencyRow'

type Props = {
  area: CompetencyArea
  index: number
  areasLength: number
  areaCompetencies: Competency[]
  isExpanded: boolean
  isEditing: boolean
  /** When inline editing a competency (area), Save is allowed only if true. */
  areaEditSaveEnabled: boolean
  editingAreaName: string
  editingAreaDesc: string
  onEditingNameChange: (v: string) => void
  onEditingDescChange: (v: string) => void
  onToggleExpand: () => void
  onStartEdit: () => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onMoveAreaUp: () => void
  onMoveAreaDown: () => void
  onDeleteAreaClick: () => void
  addingToAreaId: string | null
  newCompetencyName: string
  newCompetencyDesc: string
  onNewCompetencyNameChange: (v: string) => void
  onNewCompetencyDescChange: (v: string) => void
  onStartAddCompetency: () => void
  onCancelAddCompetency: () => void
  onSubmitNewCompetency: () => void
  onMoveCompetency: (competencyId: string, direction: 'up' | 'down') => void
  onEditCompetency: (c: Competency) => void
  onDeleteCompetencyClick: (
    competencyId: string,
    competencyName: string,
  ) => void
}

export function WheelManagementAreaCard({
  area,
  index,
  areasLength,
  areaCompetencies,
  isExpanded,
  isEditing,
  areaEditSaveEnabled,
  editingAreaName,
  editingAreaDesc,
  onEditingNameChange,
  onEditingDescChange,
  onToggleExpand,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onMoveAreaUp,
  onMoveAreaDown,
  onDeleteAreaClick,
  addingToAreaId,
  newCompetencyName,
  newCompetencyDesc,
  onNewCompetencyNameChange,
  onNewCompetencyDescChange,
  onStartAddCompetency,
  onCancelAddCompetency,
  onSubmitNewCompetency,
  onMoveCompetency,
  onEditCompetency,
  onDeleteCompetencyClick,
}: Props) {
  return (
    <div className="gw-wm__area-card">
      <div className="gw-wm__area-header">
        {isEditing ? (
          <div className="gw-wm__form-stack">
            <div>
              <label className="gw-wm__label">Competency name</label>
              <input
                type="text"
                className="gw-wm__input"
                value={editingAreaName}
                onChange={e => onEditingNameChange(e.target.value)}
              />
            </div>
            <div>
              <label className="gw-wm__label">Description</label>
              <textarea
                className="gw-wm__textarea"
                rows={2}
                value={editingAreaDesc}
                onChange={e => onEditingDescChange(e.target.value)}
              />
            </div>
            <div className="gw-wm__btn-row">
              <button
                type="button"
                className="gw-wm__btn-primary"
                disabled={!areaEditSaveEnabled}
                onClick={onSaveEdit}
              >
                <FontAwesomeIcon icon={faCheck} />
                Save
              </button>
              <button
                type="button"
                className="gw-wm__btn-secondary"
                onClick={onCancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="gw-wm__area-header-row">
            <div className="gw-wm__area-header-main">
              <button
                type="button"
                className="gw-wm__btn-icon"
                onClick={onToggleExpand}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                />
              </button>
              <div className="gw-wm__area-header-text">
                <h3 className="gw-wm__area-title">{area.name}</h3>
                <p
                  className={
                    trimDesc(area.description)
                      ? 'gw-wm__area-desc'
                      : 'gw-wm__area-desc gw-wm__desc-placeholder'
                  }
                >
                  {trimDesc(area.description) || 'No description yet.'}
                </p>
                <p className="gw-wm__area-meta">
                  {areaCompetencies.length}{' '}
                  {areaCompetencies.length === 1
                    ? 'sub-competency'
                    : 'sub-competencies'}
                </p>
              </div>
            </div>
            <div className="gw-wm__area-actions">
              <div className="gw-wm__reorder">
                <button
                  type="button"
                  className="gw-wm__btn-icon"
                  disabled={index === 0}
                  title="Move up"
                  onClick={onMoveAreaUp}
                >
                  <FontAwesomeIcon icon={faChevronUp} />
                </button>
                <button
                  type="button"
                  className="gw-wm__btn-icon"
                  disabled={index === areasLength - 1}
                  title="Move down"
                  onClick={onMoveAreaDown}
                >
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
              </div>
              <button
                type="button"
                className="gw-wm__btn-icon"
                title="Edit area"
                onClick={onStartEdit}
              >
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
              <button
                type="button"
                className="gw-wm__btn-icon gw-wm__btn-icon--danger"
                title="Delete area"
                onClick={onDeleteAreaClick}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="gw-wm__expand-body">
          {areaCompetencies.length === 0 && addingToAreaId !== area.id && (
            <div className="gw-wm__sub-empty">
              <p className="gw-wm__hint mb-1">No sub-competencies yet</p>
              <p className="gw-wm__hint-sm mb-3">
                Add a sub-competency under this competency.
              </p>
            </div>
          )}
          {areaCompetencies.length > 0 && (
            <div className="gw-wm__sub-list">
              {areaCompetencies.map((competency, compIndex) => (
                <SubCompetencyRow
                  key={competency.id}
                  competency={competency}
                  compIndex={compIndex}
                  areaCompetenciesLength={areaCompetencies.length}
                  onMove={dir => onMoveCompetency(competency.id, dir)}
                  onEdit={() => onEditCompetency(competency)}
                  onDeleteClick={() =>
                    onDeleteCompetencyClick(competency.id, competency.name)
                  }
                />
              ))}
            </div>
          )}

          {addingToAreaId === area.id ? (
            <div className="gw-wm__form-panel" style={{ marginTop: '0.75rem' }}>
              <h4 className="gw-wm__form-panel-title">
                Add new sub-competency
              </h4>
              <p className="gw-wm__hint-sm mb-2">
                Add these under a competency after you expand it.
              </p>
              <div className="gw-wm__form-stack">
                <input
                  type="text"
                  className="gw-wm__input"
                  value={newCompetencyName}
                  onChange={e => onNewCompetencyNameChange(e.target.value)}
                />
                <textarea
                  className="gw-wm__textarea"
                  rows={2}
                  value={newCompetencyDesc}
                  onChange={e => onNewCompetencyDescChange(e.target.value)}
                  placeholder="Brief description (optional)"
                />
                <div className="gw-wm__btn-row">
                  <button
                    type="button"
                    className="gw-wm__btn-primary"
                    disabled={!newCompetencyName.trim()}
                    onClick={onSubmitNewCompetency}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    Add
                  </button>
                  <button
                    type="button"
                    className="gw-wm__btn-secondary"
                    onClick={onCancelAddCompetency}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="gw-wm__add-dashed"
              onClick={onStartAddCompetency}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Sub-Competency
            </button>
          )}
        </div>
      )}
    </div>
  )
}
